// Version 6 — File Upload & JSON Save/Load

import React, { useState, useEffect } from "react";
import { jsPDF } from "jspdf";
import OTPProtected from "./LocalOTPAuth";
import { autoTable } from "jspdf-autotable";

const commonFields = [
  "Yarn",
  "Yarn dyeing",
  "Winding",
  "Knitting",
  "Mercerising",
  "Dyeing",
  "Finishing",
  "Sanforizing",
  "Process Loss in %",
  "Margin in %",
  "Comments",
];

const collarCuffFields = [
  "Yarn",
  "Loose Knit",
  "Knitting",
  "Mercerising",
  "Rewinding",
  "Collar Knitting",
  "Finishing",
  "Process Loss in %",
  "Margin in %",
  "Comments",
];

export default function FabricCostingForm() {
  const [clientName, setClientName] = useState("");
  const [costingId, setCostingId] = useState("");
  const [programs, setPrograms] = useState([
    { fabric: "", otherFabric: "", fields: {} },
  ]);

  useEffect(() => {
    if (!costingId) {
      setCostingId(`CID-${Date.now()}`);
    }
  }, []);

  const handleNumberInput = (e) => {
    const val = e.target.value;
    if (/^-?\d*\.?\d*$/.test(val)) {
      e.target.value = val;
    } else {
      e.preventDefault();
    }
  };

  const addProgram = () => {
    setPrograms([...programs, { fabric: "", otherFabric: "", fields: {} }]);
  };

  const deleteProgram = (index) => {
    if (programs.length > 1) {
      setPrograms(programs.filter((_, i) => i !== index));
    }
  };

  const updateProgramField = (index, field, value) => {
    const newPrograms = [...programs];
    newPrograms[index].fields[field] = value;
    setPrograms(newPrograms);
  };

  const updateProgramFabric = (index, fabric) => {
    const newPrograms = [...programs];
    newPrograms[index].fabric = fabric;
    if (fabric !== "Other") {
      newPrograms[index].otherFabric = "";
    }
    setPrograms(newPrograms);
  };

  const updateOtherFabric = (index, val) => {
    const newPrograms = [...programs];
    newPrograms[index].otherFabric = val;
    setPrograms(newPrograms);
  };

  /** Save JSON alongside PDF **/
  const downloadJson = (data) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `costing_${costingId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /** Handle file upload **/
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type === "application/json") {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const jsonData = JSON.parse(event.target.result);
          if (jsonData.clientName && jsonData.costingId && jsonData.programs) {
            setClientName(jsonData.clientName);
            setCostingId(jsonData.costingId);
            setPrograms(jsonData.programs);
          } else {
            alert("Invalid JSON structure.");
          }
        } catch {
          alert("Error parsing JSON file.");
        }
      };
      reader.readAsText(file);
    } else {
      alert("Please upload a valid JSON file.");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Prepare JSON data
    const jsonData = {
      clientName,
      costingId,
      programs,
    };

    // Download JSON
    downloadJson(jsonData);

    // Generate PDF
    const doc = new jsPDF();
    let y = 10;

    doc.setFontSize(16);
    doc.text("Fabric Costing Report", 10, y);
    y += 10;

    doc.setFontSize(12);
    doc.text(`Client Name: ${clientName}`, 10, y);
    y += 8;
    doc.text(`Costing ID: ${costingId}`, 10, y);
    y += 10;

    programs.forEach((program, index) => {
      doc.setFontSize(14);
      doc.text(`Program ${index + 1}`, 10, y);
      y += 6;

      const fieldsToUse =
        program.fabric === "Collar Cuff" ? collarCuffFields : commonFields;

      let sumValues = 0;
      fieldsToUse.forEach((field) => {
        if (
          field !== "Comments" &&
          field !== "Process Loss in %" &&
          field !== "Margin in %"
        ) {
          const num = parseFloat(program.fields[field]) || 0;
          sumValues += num;
        }
      });

      const processLossPercent =
        parseFloat(program.fields["Process Loss in %"]) || 0;
      const marginPercent = parseFloat(program.fields["Margin in %"]) || 0;

      const processLossAmount = (processLossPercent / 100) * sumValues;
      const totalAfterProcessLoss = sumValues + processLossAmount;

      const marginAmount = (marginPercent / 100) * totalAfterProcessLoss;
      const totalAmount = totalAfterProcessLoss + marginAmount;

      const tableBody = fieldsToUse.map((field) => [
        { content: field, styles: { halign: "center", valign: "middle" } },
        {
          content: program.fields[field] || "-",
          styles: { halign: "center", valign: "middle" },
        },
      ]);

      tableBody.push([
        {
          content: "Process Loss",
          styles: { halign: "center", valign: "middle", fontStyle: "bold" },
        },
        {
          content: totalAfterProcessLoss.toFixed(2),
          styles: { halign: "center", valign: "middle", fontStyle: "bold" },
        },
      ]);
      tableBody.push([
        {
          content: "Amount",
          styles: { halign: "center", valign: "middle", fontStyle: "bold" },
        },
        {
          content: totalAmount.toFixed(2),
          styles: { halign: "center", valign: "middle", fontStyle: "bold" },
        },
      ]);

      const topRows = [
        [
          {
            content: "Fabric",
            styles: { halign: "center", fontStyle: "bold" },
          },
          { content: program.fabric || "-", styles: { halign: "center" } },
        ],
      ];
      if (program.fabric === "Other") {
        topRows.push([
          {
            content: "Other Fabric",
            styles: { halign: "center", fontStyle: "bold" },
          },
          { content: program.otherFabric || "-", styles: { halign: "center" } },
        ]);
      }

      const body = [...topRows, ...tableBody];

      autoTable(doc, {
        startY: y,
        theme: "grid",
        styles: { fontSize: 10, halign: "center", valign: "middle" },
        body: body,
        columnStyles: {
          0: { cellWidth: 80 },
          1: { cellWidth: 60 },
        },
        margin: { left: 10, right: 10 },
      });

      y = doc.lastAutoTable.finalY + 10;

      if (y > 270 && index < programs.length - 1) {
        doc.addPage();
        y = 10;
      }
    });

    doc.save(`costing_${costingId}.pdf`);
  };

  return (
    <OTPProtected>
      <div className="min-h-screen bg-gray-100 flex justify-center items-center p-6">
        <form
          onSubmit={handleSubmit}
          className="bg-white text-black p-6 rounded-lg shadow-lg w-full max-w-7xl grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
        >
          {/* File Upload */}
          <div className="col-span-full">
            <label className="block mb-1 font-semibold">Upload JSON File</label>
            <input
              type="file"
              accept=".json,application/json"
              onChange={handleFileUpload}
              className="w-full p-2 rounded border border-gray-400"
            />
          </div>

          <div className="text-center font-extrabold border-2  border-black">
            (or)
          </div>
          {/* Client Info */}
          <div className="col-span-full">
            <label className="block mb-1 font-semibold">Client Name</label>
            <input
              type="text"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              className="w-full p-2 rounded border border-gray-400"
            />
          </div>
          <div className="col-span-full">
            <label className="block mb-1 font-semibold">Costing ID</label>
            <input
              type="text"
              value={costingId}
              readOnly
              className="w-full p-2 rounded border border-gray-400 bg-gray-200"
            />
          </div>

          {/* Programs */}
          {programs.map((program, index) => {
            const fieldsToUse =
              program.fabric === "Collar Cuff"
                ? collarCuffFields
                : commonFields;
            return (
              <div
                key={index}
                className="col-span-full border border-gray-300 rounded-lg p-4 mt-4 bg-gray-50"
              >
                <div className="flex justify-between items-center mb-3">
                  <label className="block font-semibold">Fabric</label>
                  {programs.length > 1 && (
                    <button
                      type="button"
                      onClick={() => deleteProgram(index)}
                      className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                    >
                      Delete
                    </button>
                  )}
                </div>

                <select
                  value={program.fabric}
                  onChange={(e) => updateProgramFabric(index, e.target.value)}
                  className="w-full p-2 rounded border border-gray-400"
                >
                  <option value="">Select Fabric</option>
                  <option value="SJ">SJ</option>
                  <option value="Pique">Pique</option>
                  <option value="Interlock">Interlock</option>
                  <option value="Jacquard">Jacquard</option>
                  <option value="Collar Cuff">Collar Cuff</option>
                  <option value="Other">Other</option>
                </select>

                {program.fabric === "Other" && (
                  <div className="mt-3">
                    <label className="block mb-1 font-semibold">
                      Other Fabric Name
                    </label>
                    <input
                      type="text"
                      value={program.otherFabric}
                      onChange={(e) => updateOtherFabric(index, e.target.value)}
                      className="w-full p-2 rounded border border-gray-400"
                    />
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mt-4">
                  {fieldsToUse.map((field) =>
                    field === "Comments" ? (
                      <div key={field}>
                        <label className="block mb-1 font-semibold">
                          {field}
                        </label>
                        <input
                          type="text"
                          value={program.fields[field] || ""}
                          onChange={(e) =>
                            updateProgramField(index, field, e.target.value)
                          }
                          className="w-full p-2 rounded border border-gray-400"
                        />
                      </div>
                    ) : (
                      <div key={field}>
                        <label className="block mb-1 font-semibold">
                          {field}
                        </label>
                        <input
                          type="text"
                          onInput={handleNumberInput}
                          value={program.fields[field] || ""}
                          onChange={(e) =>
                            updateProgramField(index, field, e.target.value)
                          }
                          className="w-full p-2 rounded border border-gray-400"
                        />
                      </div>
                    )
                  )}
                </div>
              </div>
            );
          })}

          {/* Buttons */}
          <div className="col-span-full flex justify-between mt-4">
            <button
              type="button"
              onClick={addProgram}
              className="px-6 py-2 bg-gray-300 text-black rounded hover:bg-gray-400 transition"
            >
              Add Another Program
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-black text-white rounded hover:bg-gray-800 transition"
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </OTPProtected>
  );
}
